// Client-side prediction with server reconciliation
// Provides smooth movement by predicting locally and correcting when server responds

import { PHYSICS_CONSTANTS } from '@shared';

interface PredictedState {
  x: number;
  y: number;
  rotation: number;
  vx: number;
  vy: number;
}

interface InputRecord {
  sequence: number;
  timestamp: number;
  accelerate: boolean;
  brake: boolean;
  steerLeft: boolean;
  steerRight: boolean;
  steerValue: number;
  nitro: boolean;
  handbrake: boolean;
}

// Store pending inputs that haven't been confirmed by server
const pendingInputs: InputRecord[] = [];
let lastConfirmedSequence = 0;
let predictedState: PredictedState | null = null;
let currentInput: InputRecord | null = null; // Current held input for continuous simulation

// Physics constants for local prediction (simplified)
const DELTA_TIME = 1 / 60; // 60fps physics
const MAX_PENDING_INPUTS = 120; // ~2 seconds at 60fps

// Helper functions
function vec2Length(x: number, y: number): number {
  return Math.sqrt(x * x + y * y);
}

function vec2Normalize(x: number, y: number): { x: number; y: number } {
  const len = vec2Length(x, y);
  if (len < 0.0001) return { x: 0, y: 0 };
  return { x: x / len, y: y / len };
}

/**
 * Set the current input state (called when input changes)
 */
export function setCurrentInput(input: InputRecord): void {
  currentInput = { ...input };
}

/**
 * Record an input for later reconciliation
 */
export function recordInput(input: InputRecord): void {
  pendingInputs.push({ ...input });
  currentInput = { ...input };
  
  // Trim old inputs to prevent memory growth
  while (pendingInputs.length > MAX_PENDING_INPUTS) {
    pendingInputs.shift();
  }
}

/**
 * Apply local prediction for immediate responsiveness
 * Call this after recording input
 */
export function predictLocalMovement(
  currentState: PredictedState,
  input: InputRecord
): PredictedState {
  const result = simulateStep(currentState, input);
  predictedState = result;
  return result;
}

/**
 * Run one frame of prediction using current held input
 * Call this each render frame for smooth continuous movement
 */
export function predictFrame(deltaTime: number = DELTA_TIME): PredictedState | null {
  if (!predictedState || !currentInput) return predictedState;
  
  // Run physics step with current input
  predictedState = simulateStep(predictedState, currentInput);
  return predictedState;
}

/**
 * Simulate one physics step with given input
 */
function simulateStep(state: PredictedState, input: InputRecord): PredictedState {
  let { x, y, rotation, vx, vy } = state;
  
  // Calculate forward direction
  const forwardX = Math.sin(rotation);
  const forwardY = -Math.cos(rotation);
  
  const speed = vec2Length(vx, vy);
  const forwardSpeed = vx * forwardX + vy * forwardY;
  
  // Max speed for prediction
  const maxSpeed = PHYSICS_CONSTANTS.MAX_SPEED;
  
  // === ACCELERATION / BRAKING ===
  if (input.accelerate && speed < maxSpeed) {
    const speedRatio = speed / maxSpeed;
    const accelerationFactor = Math.max(0.15, 1 - speedRatio * 0.85);
    let accel = PHYSICS_CONSTANTS.ENGINE_FORCE * accelerationFactor * 0.02;
    
    // Apply nitro boost if active
    if (input.nitro) {
      accel *= PHYSICS_CONSTANTS.NITRO_BOOST_MULTIPLIER;
    }
    
    vx += forwardX * accel;
    vy += forwardY * accel;
  }
  
  if (input.brake) {
    if (forwardSpeed > 0.2) {
      // Braking
      const brakeFactor = Math.max(0.9, 1 - PHYSICS_CONSTANTS.BRAKE_FORCE * 0.01);
      vx *= brakeFactor;
      vy *= brakeFactor;
    } else if (forwardSpeed > -PHYSICS_CONSTANTS.MAX_REVERSE_SPEED) {
      // Reversing
      const reverseAccel = PHYSICS_CONSTANTS.REVERSE_FORCE * 0.012;
      vx -= forwardX * reverseAccel;
      vy -= forwardY * reverseAccel;
    }
  }
  
  // === STEERING ===
  const newSpeed = vec2Length(vx, vy);
  if (newSpeed > 0.1) {
    let steerInput = 0;
    if (input.steerValue !== undefined && input.steerValue !== 0) {
      steerInput = input.steerValue;
    } else if (input.steerLeft) {
      steerInput = -1;
    } else if (input.steerRight) {
      steerInput = 1;
    }
    
    if (steerInput !== 0) {
      // Speed-based steering effectiveness (more grip at lower speeds)
      const speedFactor = Math.max(0.3, 1 - (newSpeed / maxSpeed) * 0.7);
      const steerAngle = PHYSICS_CONSTANTS.MAX_STEERING_ANGLE * steerInput * speedFactor;
      
      // Apply rotation
      const turnRate = steerAngle * (forwardSpeed > 0 ? 1 : -1) * 0.08;
      rotation += turnRate;
      
      // Rotate velocity to follow car direction (grip)
      const grip = input.handbrake ? 0.85 : 0.95;
      const currentDir = vec2Normalize(vx, vy);
      const newForwardX = Math.sin(rotation);
      const newForwardY = -Math.cos(rotation);
      
      // Blend current velocity direction toward new car direction
      const blendedX = currentDir.x * (1 - grip) + newForwardX * grip;
      const blendedY = currentDir.y * (1 - grip) + newForwardY * grip;
      const blended = vec2Normalize(blendedX, blendedY);
      
      vx = blended.x * newSpeed;
      vy = blended.y * newSpeed;
    }
  }
  
  // === DRAG ===
  const dragFactor = 1 - PHYSICS_CONSTANTS.DRAG_COEFFICIENT;
  vx *= dragFactor;
  vy *= dragFactor;
  
  // === ROLLING RESISTANCE ===
  const finalSpeed = vec2Length(vx, vy);
  if (finalSpeed > 0.1 && !input.accelerate) {
    const resistFactor = 1 - PHYSICS_CONSTANTS.ROLLING_RESISTANCE;
    vx *= resistFactor;
    vy *= resistFactor;
  }
  
  // === CLAMP SPEED ===
  const clampedSpeed = vec2Length(vx, vy);
  if (clampedSpeed > maxSpeed) {
    const scale = maxSpeed / clampedSpeed;
    vx *= scale;
    vy *= scale;
  }
  
  // === UPDATE POSITION ===
  x += vx;
  y += vy;
  
  return { x, y, rotation, vx, vy };
}

/**
 * Reconcile server state with pending predictions
 * Returns the corrected state after replaying unconfirmed inputs
 */
export function reconcileWithServer(
  serverState: PredictedState,
  serverSequence: number
): PredictedState {
  // Remove all inputs that have been processed by server
  while (pendingInputs.length > 0 && pendingInputs[0]!.sequence <= serverSequence) {
    pendingInputs.shift();
  }
  
  lastConfirmedSequence = serverSequence;
  
  // Start from server's authoritative state
  let reconciledState = { ...serverState };
  
  // Replay all pending (unconfirmed) inputs
  for (const input of pendingInputs) {
    reconciledState = simulateStep(reconciledState, input);
  }
  
  predictedState = reconciledState;
  return reconciledState;
}

/**
 * Get the current predicted state
 */
export function getPredictedState(): PredictedState | null {
  return predictedState;
}

/**
 * Initialize prediction with server state
 */
export function initializePrediction(state: PredictedState): void {
  predictedState = { ...state };
  pendingInputs.length = 0;
  lastConfirmedSequence = 0;
  currentInput = {
    sequence: 0,
    timestamp: Date.now(),
    accelerate: false,
    brake: false,
    steerLeft: false,
    steerRight: false,
    steerValue: 0,
    nitro: false,
    handbrake: false,
  };
}

/**
 * Clear all prediction state
 */
export function clearPrediction(): void {
  predictedState = null;
  pendingInputs.length = 0;
  lastConfirmedSequence = 0;
  currentInput = null;
}

/**
 * Reset prediction velocity to zero (used on respawn)
 * Keeps position but clears velocity and pending inputs
 */
export function resetPredictionVelocity(): void {
  if (predictedState) {
    predictedState.vx = 0;
    predictedState.vy = 0;
  }
  // Clear pending inputs since they contained old velocity
  pendingInputs.length = 0;
}

/**
 * Get count of pending inputs (for debugging)
 */
export function getPendingInputCount(): number {
  return pendingInputs.length;
}
